import React, { useContext, useEffect, useState } from "react";

import { fetchAvailableDomains } from "@mne-authentication/api";
import { SessionContext, Unauthenticated } from "@mne-authentication/session2";
import { searchCustomersByCriteria } from "@mne-customer/api";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";

import { FormattedMessage, defineMessages, useIntl } from "@mnemonic/intl";
import { PageLayout, RemoveSplash, TablePagination, TableSimple } from "@mnemonic/mui";
import { SearchBar } from "@mnemonic/mui";
import * as ArgusUtils from "@mnemonic/pure-utils/ArgusUtils";

import RedirectToLoginPage from "./../../utils/RedirectToLoginPage";
import { Customers, Domain } from "./Filterboxes";
import { useClientOverviewPage } from "./useClientOverviewPage";
import type { TClientColumnId } from "./useClientOverviewPage";

const messages = defineMessages({
  searchPlaceholder: { id: "client.overview.keywords", defaultMessage: "Keywords" },
});

export const ClientOverviewPage = () => {
  const apiUrl = ArgusUtils.getApiUrl();
  const { session } = useContext(SessionContext);
  const [customers, setCustomers] = useState(null);
  const [domains, setDomains] = useState(null);
  const [fetchCustomerError, setCustomerError] = useState(false);
  const { formatMessage } = useIntl();
  const {
    columns,
    isFetching,
    keywordValue,
    paginationProps,
    rows,
    setCustomerCheckboxState,
    setKeywordValue,
    updateCriteria,
  } = useClientOverviewPage();

  const opts = { apiUrl, sessionKey: "" };

  if (session) {
    opts.sessionKey = session.info.sessionKey;
  }

  // Fetch customers and available domains on initial load
  useEffect(() => {
    searchCustomersByCriteria({ limit: 0 }, { apiUrl }).then(({ json, response }) => {
      if (response.ok) {
        setCustomers(json.data);
      } else {
        setCustomerError(true);
      }
    });

    fetchAvailableDomains({}, opts).then(({ json, response }) => {
      if (response.ok) {
        setDomains(json.data);
      }
    });
  }, []);

  return (
    <PageLayout
      PageHeaderProps={{
        content: (
          <Typography variant="h6">
            <FormattedMessage id="client.overview.title" defaultMessage="External applications" />
          </Typography>
        ),
      }}
    >
      {session && (
        <>
          <RemoveSplash />
          <Grid container direction="column">
            <Grid container direction="row" mb={2}>
              <SearchBar
                placeholder={formatMessage(messages.searchPlaceholder)}
                value={keywordValue}
                onChange={(event) => setKeywordValue(event.target.value)}
                onClickSearchIcon={() => updateCriteria({ keywords: [keywordValue], offset: 0 })}
                onKeyDown={(event) => {
                  if (event.keyCode === 13) {
                    updateCriteria({ keywords: [keywordValue], offset: 0 });
                  }
                }}
                onClickClearIcon={() => {
                  setKeywordValue("");
                }}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={() => updateCriteria({ keywords: [keywordValue], offset: 0 })}
              >
                <FormattedMessage id="client.overview.search" defaultMessage="Search" />
              </Button>
            </Grid>
            <Grid container direction="row" spacing={2} mb={2}>
              <Grid>
                <Customers
                  error={
                    fetchCustomerError ? (
                      <FormattedMessage
                        id="client.overview.fetch.customers.error"
                        defaultMessage="Could not fetch customers"
                      />
                    ) : null
                  }
                  onChange={(newCustomers) => updateCriteria({ customer: newCustomers, offset: 0 })}
                  onCheckboxChange={(state) => {
                    setCustomerCheckboxState({
                      showCustomerApps: state.showCustomerApplications,
                      showNonCustomerApps: state.showNonCustomerApplications,
                    });
                    updateCriteria({ offset: 0 });
                  }}
                  options={customers || []}
                  loading={!customers}
                />
              </Grid>
              <Grid>
                {domains?.length > 1 && (
                  <Domain options={domains} onChange={(domain) => updateCriteria({ domain, offset: 0 })} />
                )}
              </Grid>
            </Grid>
            <Grid container>
              <TableContainer component={Paper}>
                {isFetching && <LinearProgress color="secondary" />}
                <TableSimple<TClientColumnId> headers={{ columns }} rows={rows} />
                <TablePagination component="div" {...paginationProps} />
              </TableContainer>
            </Grid>
          </Grid>
        </>
      )}
      <Unauthenticated>
        <RedirectToLoginPage />
      </Unauthenticated>
    </PageLayout>
  );
};
